import { formatMessage } from "lib/globalize-util";
import { IAllocationDimension } from "./allocation-dimension";
import { PURCHASE_ORDER_DOOR_GROUP_FIELD } from "./door-collection";

export enum PurchaseOrderDoorGroupFields {
    Id = "id",
    Name = "name",
    Active = "active",
}

export interface IPurchaseOrderDoorGroup {
    id: string;
    name: string;
    active: boolean;
}

export interface ICreatePurchaseOrderDoorGroup extends Omit<IPurchaseOrderDoorGroup, "id"> {}
export interface IUpdatePurchaseOrderDoorGroup extends Partial<IPurchaseOrderDoorGroup> {}

export enum PurchaseOrderDoorGroupOperationKey {
    Insert = "insert",
    Set = "set",
    Move = "move",
    Remove = "remove",
}

export type BuildPurchaseOrderDoorGroupMutateOperationParam =
    | {
          type: PurchaseOrderDoorGroupOperationKey.Insert;
          value: {
              index: number;
              value: ICreatePurchaseOrderDoorGroup;
          };
      }
    | {
          type: PurchaseOrderDoorGroupOperationKey.Set;
          value: {
              index: number;
              value: IUpdatePurchaseOrderDoorGroup;
          };
      }
    | {
          type: PurchaseOrderDoorGroupOperationKey.Remove;
          value: {
              index: number;
          };
      }
    | {
          type: PurchaseOrderDoorGroupOperationKey.Move;
          value: {
              index: number;
              destination_index: number;
          };
      };

export interface IPurchaseOrderDoorGroupOperation {
    [PurchaseOrderDoorGroupOperationKey.Insert]: {
        index: number;
        value: ICreatePurchaseOrderDoorGroup;
    };
    [PurchaseOrderDoorGroupOperationKey.Set]: {
        index: number;
        value: IUpdatePurchaseOrderDoorGroup;
    };
    [PurchaseOrderDoorGroupOperationKey.Move]: {
        index: number;
        destination_index: number;
    };
    [PurchaseOrderDoorGroupOperationKey.Remove]: {
        index: number;
    };
}

export const buildPurchaseOrderDoorGroupMutateOperation = (
    operation: PurchaseOrderDoorGroupOperationKey,
    value: IPurchaseOrderDoorGroupOperation[PurchaseOrderDoorGroupOperationKey],
): Partial<IPurchaseOrderDoorGroupOperation> | null => {
    let operationValue;
    switch (operation) {
        case PurchaseOrderDoorGroupOperationKey.Insert:
        case PurchaseOrderDoorGroupOperationKey.Set:
        case PurchaseOrderDoorGroupOperationKey.Move:
        case PurchaseOrderDoorGroupOperationKey.Remove:
            operationValue = {
                ...(value as any),
            };
            break;
        default: {
            return null;
        }
    }
    // Not intuitive way to reorder. If destination_index > index, destination_index must increment 1
    if (operation === PurchaseOrderDoorGroupOperationKey.Move) {
        if (operationValue.destination_index > operationValue.index) {
            operationValue.destination_index += 1;
        }
    }

    return {
        [operation]: operationValue,
    };
};

// ----------------------------------------

export enum DeliveryPurchaseOrderFields {
    DeliveryId = "delivery_id",
    PurchaseOrderDoorGroupId = "purchase_order_door_group_id",
    PO = "po",
}

export enum PurchaseOrderFields {
    Id = "id",
    PoNumber = "po_number",
    ShipDate = "ship_date",
    CancelDate = "cancel_date",
    Active = "active",
}

// TODO: Tech debt. This should be configurable
// Hardcoded recommendation for Ship Date and Cancel Date
interface IPurchaseOrderConstraints {
    fieldName: PurchaseOrderFields;
    required?: boolean;
    desired?: boolean;
}
export const purchaseOrderFieldsConstraints: IPurchaseOrderConstraints[] = [
    {
        fieldName: PurchaseOrderFields.ShipDate,
        desired: true,
    },
    {
        fieldName: PurchaseOrderFields.CancelDate,
        desired: true,
    },
];

// This is the interface returned by BE.
// Caveat: When saving to BE, PO fields should be nested inside `po` key.

interface IPurchaseOrder {
    id: string | null;
    po_number: string | null;
    ship_date: number | null;
    cancel_date: number | null;
    active: boolean;
}
export interface IDeliveryPurchaseOrder extends IPurchaseOrder {
    delivery_id: string;
    purchase_order_door_group_id: string;
}

// BE expects PO fields to be inside `po` key when saving, which is different format from what is returned.
export const normalizeToRequestDeliveryPurchaseOrder = (deliveryPurchaseOrder: IDeliveryPurchaseOrder) => {
    return Object.values(DeliveryPurchaseOrderFields).reduce((acc, deliveryPoField) => {
        if (deliveryPoField === DeliveryPurchaseOrderFields.PO) {
            acc[deliveryPoField] = Object.values(PurchaseOrderFields).reduce((acc2, poField) => {
                // Workaround. Null-ify `id` since BE has bug where it can't accept `id`.
                if (poField === PurchaseOrderFields.Id) {
                    acc2[poField] = null;
                } else {
                    acc2[poField] = deliveryPurchaseOrder[poField];
                }

                return acc2;
            }, {});
        } else {
            acc[deliveryPoField] = deliveryPurchaseOrder[deliveryPoField];
        }
        return acc;
    }, {});
};

export interface ICreateDeliveryPurchaseOrder extends Omit<IDeliveryPurchaseOrder, "id"> {}
export interface IUpdateDeliveryPurchaseOrder extends Partial<IDeliveryPurchaseOrder> {}

interface IReconcilePurchaseOrderDoorGroupsToMutate {
    index: number;
    deliveryPurchaseOrder: IDeliveryPurchaseOrder;
}

// Helper function to reconcile delivery PO with active delivery and active doors
export const reconcilePOWithAllocationDimensionToMutate = (
    existingDeliveryPurchaseOrders: IDeliveryPurchaseOrder[],
    deliveryAllocationDimension: IAllocationDimension,
    doorAllocationDimension: IAllocationDimension,
): IReconcilePurchaseOrderDoorGroupsToMutate[] => {
    const returnList = [];

    // Valid delivery is both active and included
    const validDeliveries = deliveryAllocationDimension.dimension_values.reduce((acc, delivery) => {
        if (delivery.active && delivery.included) {
            acc[delivery.id] = true;
        }
        return acc;
    }, {});

    // Valid purchase order door group is used by door that is both active and included
    const validPODoorGroups = doorAllocationDimension.dimension_values.reduce((acc, door) => {
        if (door.active && door.included && door.fields?.[PURCHASE_ORDER_DOOR_GROUP_FIELD]?.string_value) {
            acc[door.fields[PURCHASE_ORDER_DOOR_GROUP_FIELD].string_value] = true;
        }
        return acc;
    }, {});

    const existingDeliveryAndPODoorGroup = {};
    existingDeliveryPurchaseOrders.forEach((deliveryPO, index) => {
        // Activate delivery POs that are tied to valid delivery
        // Inactivate ones that are tied to invalid delivery
        const active =
            validDeliveries[deliveryPO.delivery_id] === true &&
            validPODoorGroups[deliveryPO.purchase_order_door_group_id] === true;

        if (active !== deliveryPO.active) {
            returnList.push({
                deliveryPurchaseOrder: {
                    ...deliveryPO,
                    active,
                },
                index,
            });
        }

        const key = `${deliveryPO.delivery_id}_${deliveryPO.purchase_order_door_group_id}`;
        existingDeliveryAndPODoorGroup[key] = true;
    });

    let newTargetIndex = existingDeliveryPurchaseOrders.length;

    // IMPORTANT: To be extra safe, this has to be last since these are INSERT operations.
    // Remember: INSERT at existing index will mess up the following SET operations.

    // Loop through valid deliveries and PO Door group to see if new Delivery PO should be created
    Object.keys(validDeliveries).forEach((deliveryId) => {
        Object.keys(validPODoorGroups).forEach((poDoorGroupId) => {
            const key = `${deliveryId}_${poDoorGroupId}`;

            // Create delivery PO for delivery and PO door group if it doesn't exist
            if (!existingDeliveryAndPODoorGroup[key]) {
                const deliveryPO = {
                    delivery_id: deliveryId,
                    purchase_order_door_group_id: poDoorGroupId,
                    // Id will be auto-generated
                    id: null,
                    po_number: null,
                    ship_date: null,
                    cancel_date: null,
                    active: true,
                };
                returnList.push({
                    deliveryPurchaseOrder: deliveryPO,
                    index: newTargetIndex,
                });
                newTargetIndex++;
            }
        });
    });

    return returnList;
};

export const getDeliveryPurchaseOrderFieldLabel = (field: PurchaseOrderFields | DeliveryPurchaseOrderFields) => {
    switch (field) {
        case DeliveryPurchaseOrderFields.PurchaseOrderDoorGroupId:
            return formatMessage("PO Group");
        case PurchaseOrderFields.PoNumber:
            return formatMessage("PO Number");
        case PurchaseOrderFields.ShipDate:
            return formatMessage("Ship Date");
        case PurchaseOrderFields.CancelDate:
            return formatMessage("Cancel Date");
        case PurchaseOrderFields.Active:
            return formatMessage("Active");
        default:
            return "";
    }
};
